import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { CheckCircle, XCircle } from "lucide-react";

export default function LikenessPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [choices, setChoices] = useState({});
  const [bio, setBio] = useState("");
  const [wantsBio, setWantsBio] = useState(false);
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);
  const [badLink, setBadLink] = useState(false);

  const load = () => {
    const req = token ? api.get(`/public/likeness/${token}`) : api.get("/my/website-scenes");
    return req.then(({ data: d }) => {
      setData(d);
      if (d.consent) {
        setChoices(d.consent.choices || {});
        setBio(d.consent.bio || "");
        setWantsBio(!!d.consent.bio);
      }
    }).catch((e) => {
      if (token && e?.response?.status === 404) setBadLink(true);
      else setData({ scenes: [], consent: null, legal_text: "" });
    });
  };
  useEffect(() => { load(); }, [token]);

  if (badLink) return (
    <div className="mx-auto max-w-xl p-10 text-center" data-testid="likeness-bad-link">
      <h1 className="font-heading text-xl font-bold text-stone-900">That message was sent in error — please disregard it</h1>
      <p className="mt-3 text-sm leading-relaxed text-stone-500">Nothing was published and there is nothing for you to do. We're sorry for any confusion. If you have questions, please call the office at 800-719-6912.</p>
    </div>
  );
  if (!data) return <div className="p-10 text-center text-stone-400">Loading…</div>;
  const scenes = data.scenes || [];
  const allAnswered = scenes.length > 0 && scenes.every((s) => choices[s.id]);

  const submit = async () => {
    if (!allAnswered) { toast.error("Approve or decline each photo first"); return; }
    if (signature.trim().length < 3) { toast.error("Type your full name to sign"); return; }
    setBusy(true);
    try {
      const url = token ? `/public/likeness/${token}/consent` : "/my/website-scenes/consent";
      await api.post(url, { choices, bio: wantsBio ? bio : "", signature: signature.trim() });
      toast.success("Thank you — your choices are saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="mx-auto max-w-3xl p-6" data-testid="likeness-page">
      <h1 className="font-heading text-2xl font-bold text-stone-900">
        {token && data.name ? `${data.name.split()[0]}, your photos on our website` : "Your photos on our website"}
      </h1>
      <p className="mt-2 rounded-lg border border-stone-200 bg-stone-50 p-4 text-sm leading-relaxed text-stone-600" data-testid="likeness-legal">{data.legal_text}</p>

      {scenes.length === 0 ? (
        <p className="mt-6 text-sm text-stone-500" data-testid="likeness-none">No website photos feature you yet — when they do, they'll appear here for your approval.</p>
      ) : (
        <>
          <div className="mt-6 space-y-4">
            {scenes.map((s) => {
              const c = choices[s.id];
              return (
                <div key={s.id} className={`rounded-xl border p-3 shadow-sm ${c === "approved" ? "border-[#27AE60]/50" : c === "declined" ? "border-[#C0392B]/40" : "border-stone-200"}`} data-testid={`likeness-scene-${s.id}`}>
                  <img src={s.url} alt={s.title} className="w-full rounded-lg" loading="lazy" />
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-stone-700">{s.title}</p>
                    <div className="flex gap-2">
                      <button onClick={() => setChoices((m) => ({ ...m, [s.id]: "approved" }))}
                        className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${c === "approved" ? "bg-[#27AE60] text-white" : "border border-stone-300 bg-white text-stone-600 hover:bg-stone-50"}`}
                        data-testid={`likeness-approve-${s.id}`}><CheckCircle size={13} /> Use it</button>
                      <button onClick={() => setChoices((m) => ({ ...m, [s.id]: "declined" }))}
                        className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${c === "declined" ? "bg-[#C0392B] text-white" : "border border-stone-300 bg-white text-stone-600 hover:bg-stone-50"}`}
                        data-testid={`likeness-decline-${s.id}`}><XCircle size={13} /> Not this one</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
            <label className="flex items-center gap-2 text-sm font-semibold text-stone-800">
              <input type="checkbox" checked={wantsBio} onChange={(e) => setWantsBio(e.target.checked)} className="accent-[#6a3e97]" data-testid="likeness-wants-bio" />
              I'd like a short bio about me on the website (optional)
            </label>
            {wantsBio && (
              <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={4} maxLength={800}
                placeholder="A few sentences — how long you've been caregiving, what you love about it, anything you're comfortable sharing."
                className="mt-2 w-full rounded-lg border border-stone-300 p-3 text-sm" data-testid="likeness-bio" />
            )}
            <div className="mt-4">
              <label className="text-xs font-semibold uppercase tracking-wide text-stone-500">Sign — type your full legal name</label>
              <input value={signature} onChange={(e) => setSignature(e.target.value)}
                className="mt-1 w-full rounded-lg border border-stone-300 p-2.5 text-sm" data-testid="likeness-signature" />
            </div>
            <Button onClick={submit} disabled={busy || !allAnswered} className="mt-4 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="likeness-submit">
              {busy ? "Saving…" : data.consent ? "Update my choices" : "Sign & save my choices"}
            </Button>
            {data.consent && <p className="mt-2 text-xs text-stone-400">Last signed {String(data.consent.signed_at).slice(0, 10)} by {data.consent.signature}</p>}
          </div>
        </>
      )}
    </div>
  );
}
